import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router
} from '@angular/router';
import { environment } from '@env';
import { Angulartics2GoogleGlobalSiteTag } from 'angulartics2';
import { merge } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import {
  APP_ROUTES,
  AUTH_ERROR_CODES,
  AUTH_KEYS,
  isInboundProductTransition,
  PAGE_TITLES,
  removeInboundProductTransitionMarker
} from '@core/constants';
import { AuthService, cloudWatchLogOutputFactory, Logger, LogRelayService } from '@core/services';

const log = new Logger('App');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  showInboundProductTransition = this.initializeInboundProductTransition();
  private autoLogin!: boolean;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private logRelay: LogRelayService,
    private authService: AuthService,
    // do not remove the analytics injection, even if the call in ngOnInit() is removed
    // this injection initializes page tracking through the router
    private angulartics: Angulartics2GoogleGlobalSiteTag,
    private titleService: Title
  ) {
    const queryStringParams = new URLSearchParams(window.location.search);
    this.autoLogin = !!queryStringParams.get('autologin');
  }

  ngOnInit(): void {
    // Logger.outputs.push(cloudWatchLogOutputFactory(this.logRelay));

    if (environment.production) {
      Logger.enableProductionMode();
    }

    if (this.autoLogin && !this.authService.isAuthenticated) {
      this.authService.cognitoSignIn();
    }

    this.angulartics.startTracking();
    this.angulartics.eventTrack(environment.version || 'dev', {
      category: 'App initialized'
    });

    const onNavigationEnd = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    );
    const onTerminalNavigationFailure = this.router.events.pipe(
      filter(event => this.isTerminalNavigationFailure(event))
    );

    onTerminalNavigationFailure.subscribe(() => this.recoverInboundProductTransition());

    merge(onNavigationEnd)
      .pipe(
        tap(() => this.completeInboundProductTransition()),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        switchMap(route => route.data)
      )
      .subscribe(event => {
        const title = event['title']
          ? `${PAGE_TITLES.DEFAULT} - `.concat(event['title'])
          : event['title'];

        this.titleService.setTitle(title);
      });
  }

  private initializeInboundProductTransition(): boolean {
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!isInboundProductTransition(currentUrl)) {
      return false;
    }

    window.history.replaceState(
      window.history.state,
      '',
      removeInboundProductTransitionMarker(currentUrl)
    );
    return true;
  }

  private completeInboundProductTransition(): void {
    if (!this.showInboundProductTransition) {
      return;
    }

    this.showInboundProductTransition = false;
  }

  private recoverInboundProductTransition(): void {
    if (!this.showInboundProductTransition) {
      return;
    }

    this.showInboundProductTransition = false;
    void this.router.navigate([APP_ROUTES.BASE], {
      queryParams: {
        [AUTH_KEYS.AUTH_ERROR]: AUTH_ERROR_CODES.AUTHORIZATION_UNAVAILABLE
      },
      replaceUrl: true
    });
  }

  private isTerminalNavigationFailure(event: unknown): boolean {
    return (
      event instanceof NavigationError ||
      (event instanceof NavigationCancel &&
        event.code !== NavigationCancellationCode.Redirect &&
        event.code !== NavigationCancellationCode.SupersededByNewNavigation)
    );
  }
}
